/**
 * Launch promo countdown - ends Sunday 5 July 2026 at 18:00 Paris time.
 * Paris is UTC+2 in July (CEST) → 18:00 Paris = 16:00 UTC.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lightning, Clock } from "@phosphor-icons/react";

const PROMO_END_UTC = Date.UTC(2026, 6, 5, 16, 0, 0); // months are 0-indexed (6=July)

function diffParts(ms) {
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { days, hours, minutes, seconds };
}

function Unit({ value, labelKey }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center px-3 sm:px-4">
      <span className="font-heading text-2xl sm:text-3xl font-bold tabular-nums text-red-600 leading-none">
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] text-red-700/80">
        {t(labelKey)}
      </span>
    </div>
  );
}

export default function PromoCountdown() {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const parts = useMemo(() => diffParts(PROMO_END_UTC - now), [now]);

  if (!parts) return null; // promo ended, hide

  return (
    <div
      data-testid="promo-countdown"
      className="mb-10 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 rounded-3xl border-2 border-red-200 bg-gradient-to-r from-red-50 via-red-50/40 to-red-50 px-6 py-5 sm:px-8 sm:py-6"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-white shadow-sm">
          <Lightning size={18} weight="fill" />
        </span>
        <div>
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-red-700">
            {t("programme.promo.kicker")}
          </p>
          <p className="font-heading text-base sm:text-lg font-bold text-slate-900 leading-tight">
            {t("programme.promo.title")}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 divide-x divide-red-200">
        <span className="hidden sm:inline-flex items-center gap-1.5 pr-3 text-[10px] font-bold uppercase tracking-[0.18em] text-red-700/80">
          <Clock size={12} weight="regular" />
          {t("programme.promo.ends_in")}
        </span>
        <Unit value={parts.days} labelKey="programme.promo.days" />
        <Unit value={parts.hours} labelKey="programme.promo.hours" />
        <Unit value={parts.minutes} labelKey="programme.promo.minutes" />
        <Unit value={parts.seconds} labelKey="programme.promo.seconds" />
      </div>
    </div>
  );
}
